import { useMemo } from 'react';

import { ChakraBox, ChakraFlex, Text } from '@equip.health/ui';

import DateView from '~/components/maud/patients/treatmentEpisode/DateView';
import { PauseDetails } from '~/components/maud/patients/treatmentEpisode/Types';
import UniversalSelectTextView from '~/components/maud/patients/treatmentEpisode/UniversalSelectTextView';
import { useGetTreatmentEpisodeStatusReasonsEnumQuery } from '~/store/services/enumerations';
import { InputColumn } from './InputColumn';
import DeleteRowIcon from './DeleteRowIcon';
import { formatProgramDates } from './util';

export interface PauseProps {
  data: PauseDetails;
  index: number;
  isActiveStatus?: boolean;
  isReadOnly: boolean;
  onChange: (index: number, update: Partial<PauseDetails>) => void;
  onDelete: (index: number) => void;
}

const Pause = ({
  data,
  index,
  isReadOnly,
  onDelete,
  onChange,
  isActiveStatus,
}: PauseProps) => {
  const { data: episodeReasons } =
    useGetTreatmentEpisodeStatusReasonsEnumQuery();

  const pausedReasons: string[] = useMemo(
    () =>
      (episodeReasons || [])
        .filter(({ status }) => status === 'PAUSED')
        .map(({ label }) => label),
    [episodeReasons],
  );

  const TOP_LEVEL_ID = `treatment-episode-pause-${index}`;
  const PAUSED_REASON_ID = `${TOP_LEVEL_ID}__paused-reason`;
  const PAUSED_START_DATE_ID = `${TOP_LEVEL_ID}__start-date`;
  const PAUSED_END_DATE_ID = `${TOP_LEVEL_ID}__end-date`;

  return (
    <ChakraFlex direction="column" gap="12px" width="100%">
      <ChakraFlex
        alignItems="flex-end"
        flexDirection="row"
        gap="24px"
        width="100%"
      >
        <InputColumn
          id={PAUSED_REASON_ID}
          isRequired
          label={!isReadOnly ? 'Paused reason' : ''}
        >
          <UniversalSelectTextView
            id={PAUSED_REASON_ID}
            isReadOnly={isReadOnly || !isActiveStatus}
            onChange={(value) => onChange(index, { reason: value })}
            options={pausedReasons}
            value={data.reason}
          />
        </InputColumn>
        {isReadOnly ? (
          <InputColumn id={PAUSED_START_DATE_ID} isRequired label="">
            <ChakraBox>
              <Text color="neutral.secondary" variant="body">
                {formatProgramDates(data.start, data.end)}
              </Text>
            </ChakraBox>
          </InputColumn>
        ) : (
          <>
            <InputColumn
              id={PAUSED_START_DATE_ID}
              isRequired
              label="Start date"
            >
              <DateView
                id={PAUSED_START_DATE_ID}
                isReadOnly={isReadOnly || !isActiveStatus}
                maxDate={data.end}
                onChange={(value) => onChange(index, { start: value })}
                value={data.start}
              />
            </InputColumn>
            <InputColumn
              id={PAUSED_END_DATE_ID}
              isRequired={false}
              label="End date"
            >
              <DateView
                id={PAUSED_END_DATE_ID}
                isReadOnly={isReadOnly || !isActiveStatus}
                minDate={data.start}
                onChange={(value) => onChange(index, { end: value })}
                value={data.end}
              />
            </InputColumn>
          </>
        )}
        {!isReadOnly && isActiveStatus && (
          <DeleteRowIcon index={index} onClick={() => onDelete(index)} />
        )}
      </ChakraFlex>
    </ChakraFlex>
  );
};

export default Pause;
